import { useEffect, useState } from "react"
import type { CSSProperties } from "react"
import { useAuthStore } from "@/features/auth/useAuthStore"
import { rgbFor } from "@/shared/colors"
import {
  DEFAULT_VOICE,
  TTS_PROVIDER_KEY,
  TTS_VOICE_KEY,
  type TTSProvider,
} from "@/features/chat/useVoiceOutput"

interface MmxVoice {
  voice_id: string
  voice_name: string
  description?: string[]
}

export function TTSSettings() {
  const [provider, setProvider] = useState<TTSProvider>(
    (localStorage.getItem(TTS_PROVIDER_KEY) as TTSProvider | null) ?? "browser"
  )
  const [voice, setVoice] = useState<string>(
    localStorage.getItem(TTS_VOICE_KEY) ?? DEFAULT_VOICE
  )
  const [voices, setVoices] = useState<MmxVoice[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // browser = clientseitig, local = feste Container-Stimme → kein Voice-Abruf
    if (provider === "browser" || provider === "local") return
    setLoading(true)
    setError(null)
    const token = useAuthStore.getState().token ?? ""
    const q = provider === "openrouter" ? "?provider=openrouter" : "?language=german"
    fetch(`/api/tts/voices${q}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
      .then((d: { voices: MmxVoice[] }) => setVoices(d.voices ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [provider])

  function changeProvider(p: TTSProvider) {
    setProvider(p)
    localStorage.setItem(TTS_PROVIDER_KEY, p)
  }

  function changeVoice(v: string) {
    setVoice(v)
    localStorage.setItem(TTS_VOICE_KEY, v)
  }

  return (
    <div className="box overflow-hidden p-5 space-y-3" style={{ "--c": rgbFor("/profile") } as CSSProperties}>
      <div>
        <h2 className="text-sm font-semibold text-zinc-200">Sprachausgabe</h2>
        <p className="text-xs text-zinc-500 mt-0.5">Wähle die Stimme für vorgelesene Antworten.</p>
      </div>
      <div className="space-y-2">
        <label className="block text-xs text-zinc-400">Anbieter</label>
        <div className="flex gap-2">
          <button
            onClick={() => changeProvider("browser")}
            className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
              provider === "browser"
                ? "bg-violet-500/20 border-violet-500/40 text-violet-200"
                : "bg-white/[3%] border-white/[8%] text-zinc-400 hover:text-zinc-200"
            }`}
          >
            Browser (Standard)
          </button>
          <button
            onClick={() => changeProvider("minimax")}
            className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
              provider === "minimax"
                ? "bg-violet-500/20 border-violet-500/40 text-violet-200"
                : "bg-white/[3%] border-white/[8%] text-zinc-400 hover:text-zinc-200"
            }`}
          >
            MiniMax (Cloud, hochwertig)
          </button>
          <button
            onClick={() => changeProvider("local")}
            className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
              provider === "local"
                ? "bg-violet-500/20 border-violet-500/40 text-violet-200"
                : "bg-white/[3%] border-white/[8%] text-zinc-400 hover:text-zinc-200"
            }`}
          >
            Lokal (Piper)
          </button>
          <button
            onClick={() => changeProvider("openrouter")}
            className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
              provider === "openrouter"
                ? "bg-violet-500/20 border-violet-500/40 text-violet-200"
                : "bg-white/[3%] border-white/[8%] text-zinc-400 hover:text-zinc-200"
            }`}
          >
            OpenRouter (TTS-Modell)
          </button>
        </div>
      </div>

      {provider === "local" && (
        <p className="text-xs text-zinc-500">
          Lokales Piper-TTS (incus-Container, Port 10200) — feste Container-Stimme, kein Cloud-Key nötig.
          Falls nicht installiert: Voice-Interface über den Install-Button einrichten.
        </p>
      )}

      {(provider === "minimax" || provider === "openrouter") && (
        <div className="space-y-2">
          <label className="block text-xs text-zinc-400">Stimme</label>
          {loading && <p className="text-xs text-zinc-500">Lade Stimmen…</p>}
          {error && <p className="text-xs text-rose-400">Fehler: {error}</p>}
          {!loading && !error && voices.length > 0 && (
            <select
              value={voice}
              onChange={(e) => changeVoice(e.target.value)}
              className="w-full max-w-md bg-zinc-900 border border-white/[10%] rounded-lg px-3 py-1.5 text-sm text-zinc-200"
            >
              {voices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.voice_name} — {v.description?.[0] ?? v.voice_id}
                </option>
              ))}
            </select>
          )}
          {!loading && !error && voices.length === 0 && (
            <p className="text-xs text-zinc-500">
              {provider === "openrouter"
                ? "Keine Stimmen — TTS-Modell auf der LLM-Seite (Media-Modelle) wählen & OpenRouter-Key gesetzt?"
                : "Keine Stimmen gefunden — mmx CLI installiert & eingeloggt?"}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
